import { useCallback, useMemo } from 'react';
import { addDays, addYears, isAfter, isSameDay, startOfDay } from 'date-fns';

import { useDayViewActions, useDayViewState } from 'stores/dischargeCalendarStore';

import useSnfLaunchDate from './useSnfLaunchDate';

export interface UseDayNavigationResult {
  startDate: Date;
  endDate: Date;
  goToNextDay: () => void;
  goToPreviousDay: () => void;
  goToToday: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isCurrentDay: boolean;
}

export default function useDayNavigation(): UseDayNavigationResult {
  const { startDate, endDate } = useDayViewState();
  const actions = useDayViewActions();
  const snfLaunchDate = useSnfLaunchDate();

  const today = useMemo(() => startOfDay(new Date()), []);

  const isCurrentDay = useMemo(() => isSameDay(startDate, today), [startDate, today]);

  const canGoPrevious = useMemo(() => {
    if (!snfLaunchDate) return true;
    return isAfter(startDate, startOfDay(snfLaunchDate));
  }, [startDate, snfLaunchDate]);

  const maxDate = useMemo(() => addYears(new Date(), 2), []);

  const canGoNext = useMemo(() => {
    const nextDay = addDays(startDate, 1);
    return !isAfter(nextDay, startOfDay(maxDate));
  }, [startDate, maxDate]);

  const goToNextDay = useCallback(() => {
    if (canGoNext) {
      actions.goToNextDay();
    }
  }, [canGoNext, actions]);

  const goToPreviousDay = useCallback(() => {
    if (canGoPrevious) {
      actions.goToPreviousDay();
    }
  }, [canGoPrevious, actions]);

  return {
    startDate,
    endDate,
    goToNextDay,
    goToPreviousDay,
    goToToday: actions.goToToday,
    canGoNext,
    canGoPrevious,
    isCurrentDay,
  };
}
